import React, { useState } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ScrollArea } from './ui/scroll-area';
import {
  HelpCircle, Search, Book, Video, Mail, ChevronRight,
  Users, Briefcase, CreditCard, Clock, Star, FileText,
  CheckCircle, Play, X, ExternalLink, MessageCircle
} from 'lucide-react';

// Help Center content organized by user type
const HELP_CONTENT = {
  client: {
    label: 'Client Help',
    categories: [
      {
        id: 'booking',
        title: 'Booking Talent',
        icon: Users,
        articles: [
          {
            id: 'how-to-post-gig',
            title: 'How to Post a Gig',
            summary: 'Learn how to create and post a new gig to find talent',
            steps: [
              'Navigate to your Dashboard and click "Post a Gig"',
              'Select the role(s) you need (Bartender, Server, etc.)',
              'Fill in event details: date, time, location, and pay rate',
              'Use AI to generate a professional job description',
              'Select dress code and grooming requirements',
              'Review and publish your gig'
            ]
          },
          {
            id: 'how-to-book-talent',
            title: 'How to Book Talent',
            summary: 'Discover how to find and book the perfect talent for your event',
            steps: [
              'Go to "Discover Talent" from your dashboard',
              'Use filters to find talent by role, location, and availability',
              'Review talent profiles, ratings, and portfolios',
              'Click "Book Now" to send a booking request',
              'Wait for talent to confirm (you\'ll receive a notification)',
              'Manage your bookings from the Bookings page'
            ]
          },
          {
            id: 'ai-ambassadors',
            title: 'Using AI Ambassadors',
            summary: 'Learn about our digital AI talent for virtual events',
            steps: [
              'Navigate to "AI Ambassadors" from the main menu',
              'Browse available AI Ambassador profiles',
              'Select a tier: Standard, Premium, or Luxury',
              'Customize the ambassador\'s script and appearance',
              'Complete payment to confirm your booking',
              'Access your AI Ambassador dashboard for the event'
            ]
          }
        ]
      },
      {
        id: 'payments',
        title: 'Payments & Invoices',
        icon: CreditCard,
        articles: [
          {
            id: 'how-payments-work',
            title: 'How Payments Work',
            summary: 'Understand the payment process for hiring talent',
            steps: [
              'Payments are processed after the gig is completed',
              'Talent clocks in and out for accurate hours',
              'Review timesheets before approving payment',
              'Invoices are generated automatically',
              'Pay via credit card or bank transfer',
              'View payment history in your Reports section'
            ]
          },
          {
            id: 'invoices',
            title: 'Managing Invoices',
            summary: 'Learn how to view, download, and pay invoices',
            steps: [
              'Go to "Invoices" from your dashboard',
              'View pending and paid invoices',
              'Click on an invoice to see details',
              'Download PDF for your records',
              'Pay outstanding invoices directly from the platform'
            ]
          }
        ]
      },
      {
        id: 'managing-events',
        title: 'Managing Events',
        icon: Briefcase,
        articles: [
          {
            id: 'edit-gig',
            title: 'Editing a Gig',
            summary: 'Make changes to your posted gigs',
            steps: [
              'Go to "My Shifts" from your dashboard',
              'Find the gig you want to edit',
              'Click the edit icon or "Manage" button',
              'Update details as needed (note: some changes may require talent reconfirmation)',
              'Save your changes'
            ]
          },
          {
            id: 'cancel-booking',
            title: 'Canceling a Booking',
            summary: 'How to cancel bookings and understand policies',
            steps: [
              'Navigate to your Bookings page',
              'Find the booking you need to cancel',
              'Click "Cancel Booking"',
              'Review cancellation policy and any fees',
              'Confirm cancellation',
              'Note: Cancellation policies vary based on timing'
            ]
          }
        ]
      },
      {
        id: 'dress-code',
        title: 'Dress Code & Grooming',
        icon: Star,
        articles: [
          {
            id: 'dress-code-guide',
            title: 'Dress Code Guide',
            summary: 'Set clear expectations for talent attire',
            steps: [
              'When creating a gig, select from preset dress codes:',
              '• All Black - Professional black attire',
              '• Business Casual - Collared shirt, slacks',
              '• Cocktail Attire - Upscale formal wear',
              '• Casual - Clean, presentable casual',
              '• Brand-Provided - You provide uniforms',
              'Add custom notes for specific requirements'
            ]
          }
        ]
      }
    ]
  },
  talent: {
    label: 'Talent Help',
    categories: [
      {
        id: 'profile',
        title: 'Profile Setup',
        icon: Users,
        articles: [
          {
            id: 'complete-profile',
            title: 'Completing Your Profile',
            summary: 'Set up your profile to get more bookings',
            steps: [
              'Navigate to Profile Information',
              'Add your professional photo (clear, well-lit headshot)',
              'Fill in your bio with relevant experience',
              'Select your available roles (Bartender, Server, etc.)',
              'Set your location and travel radius',
              'Add your minimum hourly rate',
              'Complete your portfolio with work photos'
            ]
          },
          {
            id: 'portfolio',
            title: 'Building Your Portfolio',
            summary: 'Showcase your best work to attract clients',
            steps: [
              'Go to "Portfolio" from your dashboard',
              'Upload high-quality photos of past events',
              'Add descriptions to each image',
              'Organize photos by role or event type',
              'Keep your portfolio updated regularly'
            ]
          }
        ]
      },
      {
        id: 'getting-booked',
        title: 'Getting Booked',
        icon: Briefcase,
        articles: [
          {
            id: 'find-gigs',
            title: 'Finding Gigs',
            summary: 'Discover available opportunities near you',
            steps: [
              'Go to "Find Gigs" from your dashboard',
              'Use filters to find gigs by role, date, and location',
              'Review gig details and pay rates',
              'Apply with a personalized message',
              'Wait for client approval',
              'Confirm bookings promptly to secure the gig'
            ]
          },
          {
            id: 'boost-profile',
            title: 'Boosting Your Profile',
            summary: 'Increase visibility to get more bookings',
            steps: [
              'Navigate to "Profile Boost" in your settings',
              'Select a boost duration (1 day, 3 days, 7 days)',
              'Boosted profiles appear higher in search results',
              'Track your boost performance in analytics'
            ]
          }
        ]
      },
      {
        id: 'working-gigs',
        title: 'Working Gigs',
        icon: Clock,
        articles: [
          {
            id: 'clock-in-out',
            title: 'Clock In & Out',
            summary: 'Accurate timekeeping for your shifts',
            steps: [
              'On the day of your gig, open the app',
              'Go to your confirmed booking',
              'Click "Clock In" when you arrive (GPS verification required)',
              'Complete your shift duties',
              'Click "Clock Out" when finished',
              'Add any notes about the shift if needed',
              'Your hours are automatically recorded for payment'
            ]
          },
          {
            id: 'shift-confirmation',
            title: 'Confirming Shifts',
            summary: 'Respond to booking requests on time',
            steps: [
              'When you receive a booking request, review details carefully',
              'Confirm within the deadline shown',
              'Late confirmations may result in lost bookings',
              'If you can\'t make it, decline early so clients can find replacements'
            ]
          }
        ]
      },
      {
        id: 'payments',
        title: 'Payments & Earnings',
        icon: CreditCard,
        articles: [
          {
            id: 'getting-paid',
            title: 'Getting Paid',
            summary: 'How and when you get paid depends on the specific gig',
            steps: [
              'Complete your shift and clock out — your hours are submitted for client approval',
              'Payout timing is set PER GIG — always check that gig\'s payout terms before you accept (e.g. "Within 30 business days")',
              'Every payout includes up to 5 business days for iEvntür to review and verify completed work before earnings are approved',
              'Who pays you depends on the gig: iEvntür (for iEvntür Paid / Managed gigs) or the client directly (Client Paid / Direct Booking)',
              'A gig\'s published payout terms are the schedule — an actual payment date is confirmed once your earnings are approved',
              'Add your bank details in Profile → Payouts and track everything on your Earnings page',
              'There is no universal weekly or 3–5 day payout — the terms shown on each gig always apply'
            ]
          },
          {
            id: 'instant-payout',
            title: 'Instant Pay',
            summary: 'When faster payout is available (it varies by gig)',
            steps: [
              'Instant Pay is optional and eligibility-based — it is NOT available on every gig and is not automatic',
              'Availability depends on the gig\'s payment type, your account status, and Stripe requirements (e.g. a verified debit card)',
              'If a completed gig qualifies, an Instant Pay option appears on that gig\'s eligible earnings on your Earnings page',
              'Instant Pay still follows the up-to-5-business-day completion review and verification period before earnings are approved',
              'A small processing fee applies when you choose Instant Pay',
              'Check each gig\'s payout terms — some gigs use standard or direct payout instead of Instant Pay'
            ]
          }
        ]
      },
      {
        id: 'ratings',
        title: 'Ratings & Reviews',
        icon: Star,
        articles: [
          {
            id: 'improve-ratings',
            title: 'Improving Your Ratings',
            summary: 'Tips for earning 5-star reviews',
            steps: [
              'Arrive on time (early is even better)',
              'Dress professionally according to requirements',
              'Be proactive and helpful during the event',
              'Communicate professionally with clients',
              'Follow all gig instructions carefully',
              'Leave your own honest review of the experience'
            ]
          }
        ]
      }
    ]
  }
};

// Video tutorials data — each has a steps array rendered in-modal if no video URL is configured
const VIDEO_TUTORIALS = {
  client: [
    {
      id: 'welcome-client',
      title: 'Welcome to iEvntür',
      duration: '2:30',
      type: 'welcome',
      summary: 'A quick overview of how clients use iEvntür to find and book talent.',
      steps: [
        'Post a gig with your event details, dates, and role needed',
        'Browse verified talent and send booking requests',
        'Approve hours after the shift and release payment',
        'Rate the talent so others know how they performed'
      ]
    },
    {
      id: 'post-first-gig',
      title: 'Posting Your First Gig',
      duration: '3:45',
      type: 'tutorial',
      summary: 'Step-by-step: create a gig and get it in front of talent.',
      steps: [
        'Click "Post a Gig" from the dashboard',
        'Select the role(s) you need and the date/time',
        'Enter venue name and address — Google fills the rest',
        'Set your bill rate (per hour) and talent needed',
        'Add dress code, duties, and any important notes',
        'Click Create — talent get notified instantly'
      ]
    },
    {
      id: 'using-ai-descriptions',
      title: 'Using AI Job Descriptions',
      duration: '1:50',
      type: 'tutorial',
      summary: 'Let AI write the job description for you in seconds.',
      steps: [
        'On the post-a-gig form, click "Generate with AI"',
        'Pick a tone: friendly / professional / energetic',
        'Review the generated description and tweak wording',
        'Save and publish'
      ]
    }
  ],
  talent: [
    {
      id: 'welcome-talent',
      title: 'Getting Started on iEvntür',
      duration: '2:30',
      type: 'welcome',
      summary: 'Welcome! Here\'s how to find gigs and get booked.',
      steps: [
        'Complete your profile (photo, bio, phone, city)',
        'Verify your ID so clients can trust you',
        'Sign the Independent Contractor Agreement',
        'Browse open gigs and request to be booked',
        'Once confirmed, clock in at the venue and get paid'
      ]
    },
    {
      id: 'profile-setup',
      title: 'Setting Up Your Profile',
      duration: '4:15',
      type: 'tutorial',
      summary: 'A complete profile gets 3× more bookings. Here\'s what clients look for.',
      steps: [
        'Go to Profile → Info & Security',
        'Upload a clear profile photo (face visible, well-lit)',
        'Fill in first name, last name, phone, city, and state',
        'Add your gig roles (Bartender, BA, Server, Model, etc.)',
        'Write a short bio describing your experience',
        'Save — your profile now shows up in client discovery'
      ]
    },
    {
      id: 'clock-in-tutorial',
      title: 'How to Clock In / Clock Out',
      duration: '1:30',
      type: 'tutorial',
      summary: 'Clocking in/out is how you get paid for your shift.',
      steps: [
        'Open the iEvntür app (or site) when you arrive at the venue',
        'Go to Clock In / Out from Quick Actions or Bookings',
        'Tap "Clock In" — we record the time and your location',
        'Work your shift. At the end, tap "Clock Out"',
        'Your hours are automatically submitted for client approval',
        'Once approved, payment is released on the next payout cycle'
      ]
    },
    {
      id: 'become-bookable',
      title: 'How to Become Eligible / Bookable',
      duration: '2:00',
      type: 'tutorial',
      summary: 'What\'s needed before clients can book you.',
      steps: [
        'Complete all required profile fields',
        'Upload a profile photo',
        'Verify your identity (ID front + back + selfie)',
        'Sign the Independent Contractor Agreement',
        'Keep your availability up to date so clients know when you\'re free'
      ]
    }
  ]
};

const HelpCenter = ({ userType = 'client', isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [activeTab, setActiveTab] = useState('articles');

  const content = HELP_CONTENT[userType];
  const videos = VIDEO_TUTORIALS[userType];

  // Filter articles based on search
  const filteredCategories = content.categories.map(category => ({
    ...category,
    articles: category.articles.filter(article =>
      article.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      article.summary.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.articles.length > 0);

  const handleContactSupport = () => {
    window.location.href = 'mailto:support@ievntur.com?subject=Help Request';
  };

  const handleChatWithEvie = () => {
    if (onClose) onClose();
    const path = '/talent/support?chat=1';
    window.location.assign(path);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      {/* Iter-256 — Mobile-safe Help Center modal.
            • `h-[100dvh]` on mobile so the modal owns the full viewport and
              the bottom footer never falls below the device chrome.
            • `sm:h-auto` on tablet+ so the modal keeps its drawer feel.
            • `flex flex-col` so the body scrolls and the footer stays pinned.
            • `pb-[env(safe-area-inset-bottom)]` honours iPhone X+ home indicator. */}
      <DialogContent
        className="max-w-3xl w-full h-[100dvh] sm:h-auto sm:max-h-[85vh] p-0 overflow-hidden flex flex-col rounded-none sm:rounded-lg"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        data-testid="help-center-modal"
      >
        <DialogHeader className="p-4 pb-0 border-b">
          <DialogTitle className="flex items-center gap-2 text-xl">
            <HelpCircle className="w-6 h-6 text-cyan-500" />
            Help Center
          </DialogTitle>
          <DialogDescription className="sr-only">Help Center</DialogDescription>
          
          {/* Search Bar */}
          <div className="relative mt-3 pb-3">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              placeholder="Search for help..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 bg-gray-50"
              data-testid="help-search-input"
            />
          </div>
        </DialogHeader>

        {/* 2026-06 — Chat is the PRIMARY support path (email is secondary). */}
        {userType === 'talent' && (
        <div className="px-4 pt-3 shrink-0">
          <button
            type="button"
            onClick={handleChatWithEvie}
            className="w-full flex items-center gap-3 rounded-xl border border-cyan-200 bg-gradient-to-r from-cyan-50 to-white px-4 py-3 text-left hover:border-cyan-400 transition-colors"
            data-testid="help-center-chat-evie"
          >
            <span className="w-10 h-10 rounded-full bg-cyan-500 text-white flex items-center justify-center shrink-0">
              <MessageCircle className="w-5 h-5" />
            </span>
            <span className="flex-1">
              <span className="block text-sm font-semibold text-gray-900">Chat with Evie · iEvntür Support</span>
              <span className="block text-xs text-gray-600">
                Instant answers about your payments, bookings, applications and account
              </span>
            </span>
            <span className="text-xs font-semibold text-cyan-700">Start chat →</span>
          </button>
        </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
          <div className="px-4 border-b">
            <TabsList className="w-full justify-start bg-transparent h-auto p-0 gap-4">
              <TabsTrigger 
                value="articles" 
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 rounded-none pb-3 px-1"
              >
                <Book className="w-4 h-4 mr-2" />
                Articles
              </TabsTrigger>
              <TabsTrigger 
                value="videos"
                className="data-[state=active]:bg-transparent data-[state=active]:shadow-none data-[state=active]:border-b-2 data-[state=active]:border-cyan-500 rounded-none pb-3 px-1"
              >
                <Video className="w-4 h-4 mr-2" />
                Video Tutorials
              </TabsTrigger>
            </TabsList>
          </div>

          <ScrollArea className="h-[55vh]">
            {/* Articles Tab */}
            <TabsContent value="articles" className="p-4 m-0">
              {selectedArticle ? (
                // Article Detail View
                <div className="space-y-4">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={() => setSelectedArticle(null)}
                    className="mb-2"
                  >
                    ← Back to Help Center
                  </Button>
                  
                  <h2 className="text-xl font-semibold">{selectedArticle.title}</h2>
                  <p className="text-gray-600">{selectedArticle.summary}</p>
                  
                  <div className="bg-cyan-50 rounded-lg p-4 mt-4">
                    <h3 className="font-medium text-cyan-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Step-by-Step Guide
                    </h3>
                    <ol className="space-y-3">
                      {selectedArticle.steps.map((step, index) => (
                        <li key={index} className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white text-sm flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="text-gray-700 pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ) : (
                // Category List View
                <div className="space-y-6">
                  {filteredCategories.length === 0 ? (
                    <div className="text-center py-8">
                      <Search className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                      <p className="text-gray-500">No results found for "{searchQuery}"</p>
                      <Button variant="link" onClick={() => setSearchQuery('')}>
                        Clear search
                      </Button>
                    </div>
                  ) : (
                    filteredCategories.map((category) => (
                      <div key={category.id}>
                        <h3 className="font-semibold text-lg flex items-center gap-2 mb-3">
                          <category.icon className="w-5 h-5 text-cyan-600" />
                          {category.title}
                        </h3>
                        <div className="space-y-2">
                          {category.articles.map((article) => (
                            <Card
                              key={article.id}
                              className="cursor-pointer hover:bg-gray-50 transition-colors"
                              onClick={() => setSelectedArticle(article)}
                              data-testid={`help-article-${article.id}`}
                            >
                              <CardContent className="p-3 flex items-center justify-between">
                                <div>
                                  <h4 className="font-medium text-gray-900">{article.title}</h4>
                                  <p className="text-sm text-gray-500">{article.summary}</p>
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
                              </CardContent>
                            </Card>
                          ))}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </TabsContent>

            {/* Videos Tab */}
            <TabsContent value="videos" className="p-4 m-0">
              {selectedVideo ? (
                <div className="space-y-4">
                  <Button variant="ghost" size="sm" onClick={() => setSelectedVideo(null)} className="mb-2">
                    ← Back to Video Tutorials
                  </Button>
                  <h2 className="text-xl font-semibold">{selectedVideo.title}</h2>
                  <p className="text-gray-600">{selectedVideo.summary}</p>
                  <div className="bg-cyan-50 rounded-lg p-4 mt-4">
                    <h3 className="font-medium text-cyan-900 mb-3 flex items-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Step-by-Step Guide
                    </h3>
                    <ol className="space-y-3">
                      {(selectedVideo.steps || []).map((step, index) => (
                        <li key={index} className="flex gap-3">
                          <span className="flex-shrink-0 w-6 h-6 rounded-full bg-cyan-500 text-white text-sm flex items-center justify-center">
                            {index + 1}
                          </span>
                          <span className="text-gray-700 pt-0.5">{step}</span>
                        </li>
                      ))}
                    </ol>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {videos.map((video) => (
                    <Card 
                      key={video.id} 
                      className="cursor-pointer hover:bg-gray-50 transition-colors"
                      onClick={() => setSelectedVideo(video)}
                      data-testid={`help-video-${video.id}`}
                    >
                      <CardContent className="p-3 flex items-center gap-4">
                        <div className="w-16 h-12 bg-gradient-to-br from-cyan-500 to-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Play className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <h4 className="font-medium text-gray-900">{video.title}</h4>
                          <p className="text-xs text-gray-500 mt-0.5">{video.summary}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {video.type === 'welcome' ? 'Welcome' : 'Tutorial'}
                            </Badge>
                            <span className="text-xs text-gray-500">{video.duration}</span>
                          </div>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          </ScrollArea>
        </Tabs>

        {/* Iter-256 — Contact Support footer.
              • Stacks on mobile (`flex-col`), inline on tablet+ (`sm:flex-row`).
              • `shrink-0` so the footer never gets squeezed by the scroll area.
              • `safe-area-inset-bottom` padded by parent DialogContent. */}
        <div className="border-t p-4 bg-gray-50 shrink-0">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-medium text-gray-900">Still need help?</p>
              <p className="text-sm text-gray-500">{userType === 'talent' ? 'Chat with Evie for the fastest answer' : 'Our support team is here to assist you'}</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              {userType === 'talent' && (
              <Button
                onClick={handleChatWithEvie}
                className="gap-2 w-full sm:w-auto bg-cyan-500 hover:bg-cyan-600 text-white"
                data-testid="help-center-chat-support"
              >
                <MessageCircle className="w-4 h-4" />
                Chat with Evie
              </Button>
              )}
              <Button
                onClick={handleContactSupport}
                variant="outline"
                className="gap-2 w-full sm:w-auto"
                data-testid="help-center-contact-support"
              >
                <Mail className="w-4 h-4" />
                Email instead
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Iter-255 — Floating Help Button.
// Sized small (44px = a11y min) and pinned tightly to bottom-right with
// safe-area inset support so it doesn't cover content on mobile. Was
// `bottom-20 right-6 h-14 w-14` which floated mid-screen on Pixel/iPhone.
export const HelpButton = ({ userType = 'client', className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <Button
        onClick={() => setIsOpen(true)}
        style={{
          bottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
          right: 'calc(env(safe-area-inset-right, 0px) + 16px)',
        }}
        className={`fixed h-11 w-11 p-0 rounded-full shadow-lg bg-cyan-500 hover:bg-cyan-600 z-40 ${className}`}
        data-testid="help-button"
        aria-label="Open help center"
      >
        <HelpCircle className="w-5 h-5" />
      </Button>
      <HelpCenter userType={userType} isOpen={isOpen} onClose={() => setIsOpen(false)} />
    </>
  );
};

export default HelpCenter;
